import { useEffect, useRef, useState } from 'react';
import type { OwnTeamRequest } from '@nerds-at-play/contracts';
import { EntryError, entryApi, entryMessage } from './formation-api.js';
const savedKey = 'nap-request-reference';
function savedReference(): { name: string; requestId?: string } {
  try {
    return JSON.parse(localStorage.getItem(savedKey) ?? '{}') as {
      name: string;
      requestId?: string;
    };
  } catch {
    return { name: '' };
  }
}
export function TeamRequestPanel({ onJoin }: { onJoin: (name: string) => void }) {
  const [mode, setMode] = useState<'create' | 'resume'>('create');
  const [name, setName] = useState(() => savedReference().name ?? '');
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [own, setOwn] = useState<OwnTeamRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [syncError, setSyncError] = useState('');
  const retry = useRef<{ body: string; key: string } | null>(null);
  const generation = useRef(0);
  async function refresh() {
    const current = ++generation.current;
    try {
      const result = await entryApi<OwnTeamRequest>('/api/formation/me');
      if (current !== generation.current) return;
      setOwn(result);
      setSyncError('');
    } catch (e) {
      if (current !== generation.current) return;
      if (e instanceof EntryError && e.status === 401) {
        setOwn(null);
        setSyncError('');
      } else setSyncError(entryMessage(e));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 5000);
    return () => {
      clearInterval(timer);
      generation.current++;
    };
  }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (mode === 'create' && pin !== confirmation) {
      setError('The two PINs must match.');
      return;
    }
    generation.current++;
    setBusy(true);
    setError('');
    try {
      let result: OwnTeamRequest;
      if (mode === 'create') {
        const body = JSON.stringify({ name, pin, confirmPin: confirmation });
        if (retry.current?.body !== body) retry.current = { body, key: crypto.randomUUID() };
        result = await entryApi('/api/formation/requests', 'POST', {
          name,
          pin,
          confirmPin: confirmation,
          idempotencyKey: retry.current.key,
        });
      } else {
        const saved = savedReference();
        result = await entryApi('/api/formation/resume', 'POST', {
          name,
          pin,
          ...(saved.name === name && saved.requestId ? { requestId: saved.requestId } : {}),
        });
      }
      setOwn(result);
      setPin('');
      setConfirmation('');
      retry.current = null;
      try {
        localStorage.setItem(
          savedKey,
          JSON.stringify({ name: result.request.name, requestId: result.request.requestId }),
        );
      } catch {
        /* Cookie still restores status. */
      }
    } catch (e) {
      setError(entryMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function leave() {
    generation.current++;
    setBusy(true);
    setError('');
    try {
      await entryApi('/api/formation/session', 'DELETE');
      setOwn(null);
      setPin('');
      setConfirmation('');
      retry.current = null;
      try {
        localStorage.removeItem(savedKey);
      } catch {
        /* Reference is not authority. */
      }
    } catch (e) {
      setError(entryMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Team request">
      {loading && <p role="status">Checking your request…</p>}
      {syncError && (
        <p role="alert" className="entry-error">
          Status may be out of date. {syncError}
        </p>
      )}
      {error && (
        <p role="alert" className="entry-error">
          {error}
        </p>
      )}
      {own ? (
        <>
          <h3>{own.request.name}</h3>
          <p className="entry-status" role="status">
            {own.request.status === 'PENDING'
              ? 'Waiting for GM approval'
              : own.request.status === 'APPROVED'
                ? 'Team approved'
                : 'Request rejected'}
          </p>
          {own.request.status === 'PENDING' && (
            <p>
              {own.formationOpen
                ? 'Your request is saved. The Game Master can approve it even if you close this page. This status updates automatically.'
                : 'New teams are now closed. This request cannot be approved. Ask the Game Master about joining an existing team.'}
            </p>
          )}
          {own.request.status === 'REJECTED' && (
            <p>
              Ask the Game Master in person if you have questions. The name is available for a new
              request; this rejection stays in your request history.
            </p>
          )}
          {own.request.status === 'APPROVED' && (
            <>
              <p>
                Your approval is saved. Teammates will join using your team name and shared PIN.
              </p>
              <button onClick={() => onJoin(own.request.name)}>Join approved team</button>
              <p className="entry-detail">
                Approval has not logged you in. Enter your PIN again to join the approved team.
              </p>
            </>
          )}
          <div className="entry-actions">
            <button disabled={busy} onClick={() => void refresh()}>
              Refresh status
            </button>
            <button disabled={busy} onClick={() => void leave()}>
              {own.request.status === 'REJECTED' ? 'Start another request' : 'Leave request status'}
            </button>
          </div>
        </>
      ) : (
        !loading && (
          <>
            <div className="entry-actions" aria-label="Request options">
              <button
                aria-pressed={mode === 'create'}
                disabled={busy}
                onClick={() => {
                  setMode('create');
                  setError('');
                }}
              >
                Create a team
              </button>
              <button
                aria-pressed={mode === 'resume'}
                disabled={busy}
                onClick={() => {
                  setMode('resume');
                  setError('');
                }}
              >
                Check request status
              </button>
            </div>
            <p>
              {mode === 'create'
                ? 'Choose a team name and six-digit shared PIN. The Game Master must approve your request before your team can join.'
                : 'Enter the name and PIN you used to request your team. This checks request status; it does not log into a team workspace.'}
            </p>
            <form onSubmit={(event) => void submit(event)}>
              <label>
                Team entry name
                <input
                  required
                  maxLength={80}
                  autoComplete="off"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={busy}
                />
              </label>
              <label>
                Shared PIN
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  required
                  autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                  value={pin}
                  onChange={(event) => setPin(event.target.value)}
                  disabled={busy}
                />
              </label>
              {mode === 'create' && (
                <label>
                  Confirm shared PIN
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    minLength={6}
                    maxLength={6}
                    required
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    disabled={busy}
                  />
                </label>
              )}
              <p className="entry-detail">
                Keep the PIN with your teammates. If you forget it, ask the Game Master to set a new
                one in person.
              </p>
              <button className="board-primary" disabled={busy}>
                {busy ? 'Saving…' : mode === 'create' ? 'Request team access' : 'Check my request'}
              </button>
            </form>
          </>
        )
      )}
    </section>
  );
}
